import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Package, Ship, Wind } from 'lucide-react';
import { motion } from 'framer-motion';
import Hero from '../components/Hero';
import { siteImages } from '../data';
import SEO from '../components/SEO';
import SchemaMarkup from '../components/SchemaMarkup';
import { getOptimizedImageUrl } from '../lib/utils';
import { useLanguage } from '../context/LanguageContext';

const iconMap = {
  'offshore-vind': Wind,
  'offshore-wind': Wind,
  'logistik': Package,
  'logistics': Package,
  'marine-operationer': Ship,
  'marine-operations': Ship,
};

export default function Services() {
  const { lang, t, services } = useLanguage();

  const breadcrumbs = lang === 'da' 
    ? [
        { name: 'Forside', href: '/' },
        { name: 'Ydelser', href: '/ydelser' }
      ]
    : [
        { name: 'Home', href: '/en' },
        { name: 'Services', href: '/en/services' }
      ];

  return (
    <div className="bg-white">
      <SEO 
        title={t('seo.services.title')} 
        description={t('seo.services.description')}
      />
      <SchemaMarkup type="breadcrumbs" data={breadcrumbs} />

      <Hero
        title={t('services')}
        subtitle={t('servicesPage.desc')}
        image={siteImages.marineOperations}
        showCta={false}
      />

      <section className="py-20 lg:py-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12 lg:mb-16">
          <p className="text-sm font-bold text-blue-700 uppercase tracking-[0.3em] mb-4">
            {t('servicesPage.tag')}
          </p>
          <h2 className="text-3xl sm:text-5xl font-bold text-slate-950 uppercase leading-tight mb-6">
            {t('servicesPage.title')}
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            {t('servicesPage.desc')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = iconMap[service.id] || Wind;
            const serviceUrl = lang === 'da' ? `/ydelser/${service.id}` : `/en/services/${service.id}`;

            return (
              <motion.article
                key={service.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: index * 0.12 }}
                className="group relative overflow-hidden bg-white border border-slate-200 shadow-xl shadow-slate-950/5 hover:-translate-y-2 hover:shadow-2xl hover:shadow-slate-950/10 transition-all duration-300"
              >
                <div className="relative h-72 overflow-hidden bg-slate-900">
                  <img
                    src={getOptimizedImageUrl(service.image, 600, 80)}
                    srcSet={`${getOptimizedImageUrl(service.image, 300, 80)} 300w, ${getOptimizedImageUrl(service.image, 600, 80)} 600w, ${getOptimizedImageUrl(service.image, 800, 80)} 800w`}
                    sizes="(max-width: 1024px) 100vw, 400px"
                    alt={`Porthos Maritime - ${service.title}`}
                    className="h-full w-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent"></div>
                  <div className="absolute left-6 bottom-6 flex h-14 w-14 items-center justify-center bg-blue-700 text-white shadow-lg">
                    <Icon size={28} />
                  </div>
                </div>

                <div className="p-8 flex min-h-[320px] flex-col">
                  <h3 className="text-2xl font-bold text-slate-950 uppercase tracking-wide mb-4">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-6">
                    {service.shortDescription}
                  </p>

                  <div className="mb-8 grid gap-3">
                    {service.features.slice(0, 3).map((feature) => (
                      <div key={feature} className="flex items-center gap-3 text-sm font-semibold text-slate-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-700"></span>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to={serviceUrl}
                    className="mt-auto inline-flex items-center justify-between gap-3 bg-slate-950 px-6 py-4 text-sm font-bold uppercase tracking-widest text-white hover:bg-blue-700 transition-colors"
                  >
                    {t('readMore')}
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
